import React from 'react';

import { styled } from '@mui/material/styles';

import { useQueryProtocolInstance } from 'client/app/apps/protocols/api/ProtocolsAPI';
import { ProtocolInstanceProvider } from 'client/app/apps/protocols/context/ProtocolInstanceProvider';
import { SimulationProvider } from 'client/app/apps/protocols/context/SimulationProvider';
import { StepsProvider } from 'client/app/apps/protocols/context/StepsProvider/StepsProvider';
import { useConfiguredDeviceMapping } from 'client/app/apps/protocols/context/useConfiguredDeviceMapping';
import { WorkflowProvider } from 'client/app/apps/protocols/context/WorkflowProvider';
import { EditProtocolInstance } from 'client/app/apps/protocols/EditProtocolInstance';
import UIErrorBox from 'client/app/components/UIErrorBox';
import { ConfiguredDevicesProvider } from 'client/app/state/ConfiguredDevicesProvider/ConfiguredDevicesProvider';
import LinearProgress from 'common/ui/components/LinearProgress';
import { AdditionalPanelProvider } from 'common/ui/providers/AdditionalPanelProvider';

type Props = {
  id: ProtocolInstanceId;
};

export default function EditProtocolInstanceScreen({ id }: Props) {
  const { data, loading, error } = useQueryProtocolInstance(id);
  const { instance, elementContext } = data?.protocolInstance ?? {};
  const { configuredDevices, getPathConfigDeviceIds } = useConfiguredDeviceMapping(
    instance?.protocol.workflow.workflow,
    instance?.parameters,
  );

  if (error) return <UIErrorBox>Could not load protocol run</UIErrorBox>;
  if (loading || !instance) return <LinearProgress />;

  const { protocol, workflow } = instance.protocol;
  const { Schema: schema = {} } = workflow.workflow;
  const { steps } = protocol;

  return (
    <Wrapper>
      <StepsProvider steps={steps} schema={schema}>
        <WorkflowProvider workflow={workflow} elementContext={elementContext}>
          <ProtocolInstanceProvider
            instance={instance}
            getPathConfigDeviceIds={getPathConfigDeviceIds}
          >
            <SimulationProvider>
              <ConfiguredDevicesProvider devices={configuredDevices}>
                <AdditionalPanelProvider>
                  <EditProtocolInstance />
                </AdditionalPanelProvider>
              </ConfiguredDevicesProvider>
            </SimulationProvider>
          </ProtocolInstanceProvider>
        </WorkflowProvider>
      </StepsProvider>
    </Wrapper>
  );
}

const Wrapper = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  overflowY: 'auto',
  height: '100%',
}));
